import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading } from "@/components/shared";
import { Mail, FileUp, Linkedin, ShieldCheck, ArrowLeft, Clock, Check } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const METHODS = [
  { key: "email", label: "Work email", icon: Mail, blurb: "Fastest. We send a one time code to your company email address." },
  { key: "document", label: "Offer letter or payslip", icon: FileUp, blurb: "Upload a document. A moderator checks it, then it is permanently deleted within 24 hours." },
  { key: "linkedin", label: "LinkedIn profile", icon: Linkedin, blurb: "Share your profile URL. A moderator confirms your role manually." },
];

export default function Verify() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [company, setCompany] = useState(null);
  const [status, setStatus] = useState("none");
  const [method, setMethod] = useState("email");
  const [busy, setBusy] = useState(false);

  const [workEmail, setWorkEmail] = useState("");
  const [phase, setPhase] = useState("enter"); // enter | code
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [roleNote, setRoleNote] = useState("");
  const [file, setFile] = useState(null);

  useEffect(() => {
    if (user === false) { nav("/login"); return; }
    api.get(`/companies/${id}`).then(({ data }) => setCompany(data)).catch(() => setCompany(false));
    if (user) api.get("/verify/status", { params: { company_id: id } }).then(({ data }) => setStatus(data.status)).catch(() => {});
  }, [id, user, nav]);

  const startEmail = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/verify/email/start", { company_id: id, work_email: workEmail });
      setPhase("code");
      if (data.dev_code) { setDevCode(data.dev_code); toast.info("Demo mode: your code is shown below"); }
      else toast.success("Code sent. Check your inbox.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const confirmEmail = async () => {
    setBusy(true);
    try {
      await api.post("/verify/email/confirm", { company_id: id, code });
      setStatus("verified");
      toast.success("Verified. You can write your review now.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitDocument = async () => {
    if (!file) { toast.error("Choose a file first."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("company_id", id);
      fd.append("file", file);
      await api.post("/verify/document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setStatus("pending");
      toast.success("Document received. A moderator will review it shortly.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitLinkedin = async () => {
    setBusy(true);
    try {
      await api.post("/verify/linkedin", { company_id: id, linkedin_url: linkedin, role_note: roleNote });
      setStatus("pending");
      toast.success("Submitted. A moderator will confirm your role shortly.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (company === null) return <Loading />;
  if (company === false) return <Loading label="Company unavailable" />;

  return (
    <main className="mx-auto max-w-2xl px-5 py-10 lg:px-8">
      <Link to={`/company/${id}`} className="mb-6 inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-teal-400">
        <ArrowLeft className="h-4 w-4" /> Back to {company.name}
      </Link>
      <p className="eyebrow mb-2 flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Verify employment</p>
      <h1 className="text-3xl font-bold">Prove you worked at {company.name}</h1>
      <p className="mt-3 text-slate-400">
        Only verified employees can publish here. We use this once to confirm employment. We never publish your
        email or documents, and we never tell the company who reviewed them.
      </p>

      {status === "verified" ? (
        <div data-testid="verify-success" className="card-surface mt-8 p-8 text-center">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[#E7F0EE] text-teal-600"><Check className="h-7 w-7" /></div>
          <h2 className="mt-4 text-xl font-bold">You're verified</h2>
          <p className="mt-2 text-sm text-slate-400">Your employment at {company.name} is confirmed. Time to write your review.</p>
          <button data-testid="verify-to-review" onClick={() => nav(`/company/${id}/review`)} className="btn-teal mt-6 text-sm">Write your review</button>
        </div>
      ) : status === "pending" ? (
        <div data-testid="verify-pending" className="card-surface mt-8 p-8 text-center">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-info-500/10 text-info-500"><Clock className="h-7 w-7" /></div>
          <h2 className="mt-4 text-xl font-bold">Verification in review</h2>
          <p className="mt-2 text-sm text-slate-400">A moderator is checking your evidence. You'll get a notification when it's approved, then you can publish your review.</p>
          <Link to="/dashboard" className="mt-6 inline-block text-sm text-teal-400 hover:underline">Go to your dashboard</Link>
        </div>
      ) : (
        <>
          <div className="mt-8 grid gap-3 sm:grid-cols-3">
            {METHODS.map((m) => (
              <button
                key={m.key}
                data-testid={`method-${m.key}`}
                onClick={() => setMethod(m.key)}
                className={`card-surface flex flex-col items-start gap-2 p-4 text-left transition-colors ${method === m.key ? "ring-2 ring-teal-500" : "hover:border-teal-400/40"}`}
              >
                <m.icon className={`h-5 w-5 ${method === m.key ? "text-teal-400" : "text-slate-400"}`} />
                <span className="text-sm font-semibold text-ink">{m.label}</span>
                <span className="text-xs text-slate-500">{m.blurb}</span>
              </button>
            ))}
          </div>

          <div className="card-surface mt-6 p-6 sm:p-8">
            {method === "email" && (
              phase === "enter" ? (
                <div className="space-y-4">
                  <div>
                    <Label>Work email</Label>
                    <Input data-testid="verify-email-input" type="email" value={workEmail} onChange={(e) => setWorkEmail(e.target.value)}
                      placeholder={`you@${company.domain || "company.com"}`} className="mt-1.5 border-[#DDDAD1] bg-ink-900" />
                    <p className="mt-2 text-xs text-slate-500">Use your company address, not a personal one like Gmail. Kept private, never published.</p>
                  </div>
                  <button data-testid="verify-email-send" onClick={startEmail} disabled={busy || !workEmail} className="btn-teal text-sm disabled:opacity-50">
                    {busy ? "Sending…" : "Send code"}
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <Label>Enter the 6 digit code</Label>
                    <Input data-testid="verify-code-input" value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" maxLength={6}
                      placeholder="123456" className="mt-1.5 border-[#DDDAD1] bg-ink-900 font-mono text-lg tracking-[0.3em]" />
                    {devCode && (
                      <p data-testid="dev-code" className="mt-2 rounded-lg border border-info-500/30 bg-info-500/10 px-3 py-2 text-xs text-info-500">
                        Demo mode (no email key set): your code is <span className="font-mono font-bold">{devCode}</span>
                      </p>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <button data-testid="verify-code-confirm" onClick={confirmEmail} disabled={busy || code.length < 6} className="btn-teal text-sm disabled:opacity-50">
                      {busy ? "Checking…" : "Verify"}
                    </button>
                    <button onClick={() => { setPhase("enter"); setCode(""); setDevCode(""); }} className="text-sm text-slate-400 hover:text-teal-400">Use a different email</button>
                  </div>
                </div>
              )
            )}

            {method === "document" && (
              <div className="space-y-4">
                <div>
                  <Label>Offer letter, payslip or contract</Label>
                  <input data-testid="verify-doc-input" type="file" accept=".pdf,.jpg,.jpeg,.png,.webp"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    className="mt-1.5 block w-full rounded-[8px] border border-[#DDDAD1] bg-white p-3 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-[#E7F0EE] file:px-3 file:py-1.5 file:text-teal-600" />
                  <p className="mt-2 text-xs text-slate-500">PDF, JPG, PNG or WEBP up to 10MB. Redact anything you like. Deleted within 24 hours of review.</p>
                </div>
                <button data-testid="verify-doc-submit" onClick={submitDocument} disabled={busy || !file} className="btn-teal text-sm disabled:opacity-50">
                  {busy ? "Uploading…" : "Submit for review"}
                </button>
              </div>
            )}

            {method === "linkedin" && (
              <div className="space-y-4">
                <div>
                  <Label>LinkedIn profile URL</Label>
                  <Input data-testid="verify-linkedin-input" value={linkedin} onChange={(e) => setLinkedin(e.target.value)}
                    placeholder="https://www.linkedin.com/in/your-name" className="mt-1.5 border-[#DDDAD1] bg-ink-900" />
                </div>
                <div>
                  <Label>Role and dates (optional)</Label>
                  <Textarea data-testid="verify-linkedin-note" value={roleNote} onChange={(e) => setRoleNote(e.target.value)}
                    placeholder="e.g. CRM Manager, 2021 to 2023" className="mt-1.5 border-[#DDDAD1] bg-ink-900" />
                </div>
                <button data-testid="verify-linkedin-submit" onClick={submitLinkedin} disabled={busy || !linkedin} className="btn-teal text-sm disabled:opacity-50">
                  {busy ? "Submitting…" : "Submit for review"}
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </main>
  );
}
